// ============================================================================
// ProfileDropdown.jsx — Habano's profile/avatar dropdown
//
// ⚠️ DO NOT SIMPLIFY — full dropdown required ⚠️
//
// Both guest and logged-in users get the COMPLETE menu. This file has been
// regressed/simplified by previous iterations more than once; the current
// shape is the canonical spec from the product owner. Removing any item
// (Local/Tourist toggle, Get Listed, Save for Offline, Settings,
// Language row, Log In/Log Out, Privacy/Terms footer) is a REGRESSION.
//
// Guest dropdown:
//   👤 Guest / Log In →           (identity card)
//   ──────────────────────
//   Local ◯—— Tourist             (toggle — opens ViewSelectorSheet)
//   ──────────────────────
//   🚀 Get Listed                 (gold — always visible)
//   💾 Save for Offline           (shows current state)
//   ⚙️ Settings
//   🌐 English → Español          (full wording, tap row to switch)
//   ──────────────────────
//   🔑 Log In
//   Privacy · Terms
//
// Logged-in dropdown:
//   👤 [Name] · [phone]
//   ──────────────────────
//   Local ◯—— Tourist
//   ──────────────────────
//   🚗 Vendor Dashboard           (if approved)
//   🏢 Business Dashboard         (if owner)
//   ⏳ Application Pending         (if pending)
//   ──────────────────────
//   🚀 Get Listed                 (gold)
//   💾 Save for Offline
//   ⚙️ Settings
//   🌐 English → Español          (full wording)
//   ──────────────────────
//   🚪 Sign Out
//   Privacy · Terms
// ============================================================================
import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  User as UserIcon, Settings as SettingsIcon,
  Building2, Store, Shield, LogOut, LogIn, Download, Home, Luggage,
  Rocket, CheckCircle2, Clock, Globe, ArrowRight, Smartphone, Car, Compass,
} from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import ViewSelectorSheet from "./ViewSelectorSheet";
import BusinessSelectorSheet from "./BusinessSelectorSheet";
import GetListedSheet from "./GetListedSheet";
import { useI18n } from "../lib/i18n";
import { useAudienceSettings } from "../lib/useAudienceSettings";
import { useMode } from "../lib/appMode";
import { useDiscoverAvailable } from "../lib/discoverAvailability";
import { LoginSheet } from "./LoginSheet";
import { loadMyApplications, readCachedApplicationStatus, loadMyBusinesses } from "../lib/applications";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "./ui/alert-dialog";
import { SHOW_VIEW_TOGGLE } from "../lib/featureFlags";

export default function ProfileDropdown({ user, labeled = false, testidPrefix = "header" }) {
  const { t, lang } = useI18n();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [loginOpen, setLoginOpen] = useState(false);
  const [viewSheetOpen, setViewSheetOpen] = useState(false);
  const [bizSheetOpen, setBizSheetOpen] = useState(false);
  const [getListedSheetOpen, setGetListedSheetOpen] = useState(false);
  const [pendingBadge, setPendingBadge] = useState(() => readCachedApplicationStatus());
  const [myBiz, setMyBiz] = useState([]);
  const menuRef = useRef(null);
  const isGuest = !user;

  useEffect(() => {
    if (!open) return;
    const onDown = (e) => { if (menuRef.current && !menuRef.current.contains(e.target)) setOpen(false); };
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("touchstart", onDown, { passive: true });
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("touchstart", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  useEffect(() => {
    if (!user || !open) return;
    loadMyApplications().then(setPendingBadge).catch(() => {});
    if (user.role === "business" || user.role === "admin") {
      loadMyBusinesses().then(setMyBiz).catch(() => {});
    }
  }, [open, user]);

  const doLogOut = async () => {
    try { await api.post("/auth/logout"); } catch { /* ignore */ }
    localStorage.removeItem("habano.session");
    toast.success(t("settings_toast_signed_out"));
    window.location.href = "/";
  };

  const avatar = user?.profile_photo;
  const name = user?.display_name || user?.email?.split("@")[0] || t("user_guest");
  const phone = user?.whatsapp || null;

  return (
    <div className="relative" ref={menuRef} style={{ zIndex: open ? 9999 : "auto" }}>
      {labeled ? (
        <button
          data-testid="profile-menu-trigger"
          onClick={() => setOpen((v) => !v)}
          aria-haspopup="menu"
          aria-expanded={open}
          aria-label={isGuest ? (lang === "es" ? "Crear Cuenta / Iniciar Sesión" : "Create Account / Log In") : (lang === "es" ? "Cuenta" : "Account")}
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            background: "transparent",
            border: "none",
            padding: 0,
            minHeight: 48,
            justifyContent: "flex-end",
          }}
        >
          <span
            className="grid place-items-center rounded-full overflow-hidden"
            style={isGuest
              ? {
                  width: 36, height: 36,
                  background: "rgba(232,184,75,0.12)",
                  border: "1px solid rgba(232,184,75,0.35)",
                  color: "#E8B84B",
                }
              : {
                  width: 36, height: 36,
                  background: "linear-gradient(135deg, #E8B84B, #A07820)",
                  border: "1.5px solid #E8B84B",
                  color: "#0B0B0B",
                }}
          >
            {avatar ? (
              <img src={avatar} alt="" style={{ width: 36, height: 36, objectFit: "cover", borderRadius: "50%" }} />
            ) : isGuest ? (
              <UserIcon style={{ width: 15, height: 15 }} strokeWidth={1.6} />
            ) : (
              <span style={{
                fontFamily: "'DM Sans', sans-serif",
                fontSize: 14,
                fontWeight: 700,
                color: "#0B0B0B",
                lineHeight: 1,
              }}>
                {(name?.[0] || "U").toUpperCase()}
              </span>
            )}
          </span>
          <span
            data-testid={`${testidPrefix}-account-label`}
            style={{
              fontFamily: "'DM Sans', sans-serif",
              fontSize: 9,
              fontWeight: 500,
              color: "#E8B84B",
              letterSpacing: "0.3px",
              textAlign: "center",
              marginTop: 3,
              whiteSpace: "nowrap",
            }}
          >
            {isGuest
              ? (lang === "es" ? "Entrar" : "Log In")
              : (lang === "es" ? "Cuenta" : "Account")}
          </span>
        </button>
      ) : (
        <button
          data-testid="profile-menu-trigger"
          onClick={() => setOpen((v) => !v)}
          aria-haspopup="menu"
          aria-expanded={open}
          className="grid place-items-center rounded-full border border-gold-soft hover:border-gold-strong transition-all duration-300"
          style={{ width: 40, height: 40, minWidth: 40, minHeight: 40 }}
        >
          {avatar ? (
            <img src={avatar} alt="" className="w-10 h-10 rounded-full object-cover" />
          ) : (
            <span className="text-foreground/55 text-xs uppercase tracking-widest">
              {isGuest ? <UserIcon className="w-4 h-4" strokeWidth={1.5} /> : name[0]}
            </span>
          )}
        </button>
      )}

      {open && (
        <div
          role="menu"
          data-testid="profile-menu"
          className="absolute right-0 top-12 w-72 overflow-hidden transition-opacity duration-300"
          style={{
            zIndex: 9999,
            background: "#171717",
            border: "1px solid rgba(232,184,75,0.28)",
            boxShadow: "0 30px 80px rgba(0,0,0,0.85), inset 0 1px 0 rgba(255,255,255,0.03)",
          }}
        >
          {isGuest ? (
            <GuestPanel
              t={t}
              onClose={() => setOpen(false)}
              onSignIn={() => { setOpen(false); setLoginOpen(true); }}
              onOpenViewSheet={() => setViewSheetOpen(true)}
              /* iter193 — "Get Listed" renamed to "List Your Business" and
                 now navigates to /join instead of opening the sheet. Sheet
                 code kept mounted below for any callers that still open it
                 programmatically. */
              onOpenGetListedSheet={() => { setOpen(false); nav("/join"); }}
            />
          ) : (
            <AuthedPanel
              t={t}
              user={user}
              name={name}
              avatar={avatar}
              phone={phone}
              pendingBadge={pendingBadge}
              myBiz={myBiz}
              onClose={() => setOpen(false)}
              onLogOut={() => { setOpen(false); setConfirmOpen(true); }}
              onOpenViewSheet={() => setViewSheetOpen(true)}
              onOpenBizSheet={() => { setBizSheetOpen(true); setOpen(false); }}
              onOpenGetListedSheet={() => { setOpen(false); nav("/join"); }}
            />
          )}
        </div>
      )}

      <LoginSheet open={loginOpen} onClose={() => setLoginOpen(false)} />

      {SHOW_VIEW_TOGGLE && (
        <ViewSelectorSheet
          open={viewSheetOpen}
          user={user}
          onClose={() => setViewSheetOpen(false)}
        />
      )}

      <BusinessSelectorSheet
        open={bizSheetOpen}
        businesses={myBiz}
        onClose={() => setBizSheetOpen(false)}
      />

      <GetListedSheet
        open={getListedSheetOpen}
        onClose={() => setGetListedSheetOpen(false)}
      />

      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent
          data-testid="logout-confirm-dialog"
          className="border border-gold-strong p-0 max-w-sm w-[92vw] rounded-none"
          style={{ background: "#171717" }}
        >
          <AlertDialogHeader className="px-6 pt-6 pb-2">
            <AlertDialogTitle className="font-display text-2xl text-foreground leading-tight">
              {t("auth_log_out_confirm")}
            </AlertDialogTitle>
            <AlertDialogDescription className="text-foreground/65 text-sm mt-2">
              {t("auth_log_out_confirm_body")}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="px-6 pb-5 pt-2 flex-row gap-2 justify-end">
            <AlertDialogCancel
              data-testid="logout-cancel"
              className="inline-flex items-center justify-center gap-2 px-5 bg-gold text-[hsl(var(--primary-foreground))] hover:opacity-90 text-xs uppercase tracking-widest"
              style={{ minHeight: 48 }}
            >
              {t("cancel")}
            </AlertDialogCancel>
            <AlertDialogAction
              data-testid="logout-confirm"
              onClick={doLogOut}
              className="inline-flex items-center justify-center gap-2 px-5 border border-destructive/60 text-destructive hover:bg-destructive/10 text-xs uppercase tracking-widest"
              style={{ minHeight: 48 }}
            >
              <LogOut className="w-4 h-4" strokeWidth={1.5} /> {t("auth_log_out")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

// ============================================================================
// GuestPanel — ⚠️ MUST include every section listed at the top of the file.
// ============================================================================
function GuestPanel({ t, onClose, onSignIn, onOpenViewSheet, onOpenGetListedSheet }) {
  return (
    <>
      {/* Identity card */}
      <button
        type="button"
        data-testid="profile-menu-guest-header"
        onClick={onSignIn}
        className="w-full text-left flex items-center gap-3 px-4 py-4 border-b border-gold-soft transition-colors hover:bg-white/[0.03]"
      >
        <span
          className="grid place-items-center rounded-full"
          style={{ width: 40, height: 40, background: "rgba(232,184,75,0.15)", border: "1px solid rgba(232,184,75,0.4)", color: "#E8B84B" }}
        >
          <UserIcon className="w-4 h-4" strokeWidth={1.5} />
        </span>
        <div className="min-w-0 flex-1">
          <div style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 15, color: "#F5F1E8", fontWeight: 600 }}>{t("user_guest")}</div>
          <div style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 14, color: "#E8B84B" }} data-testid="profile-menu-guest-tagline">
            {t("auth_log_in")} →
          </div>
        </div>
      </button>

      {/* Local / Tourist toggle — hidden via SHOW_VIEW_TOGGLE flag (iter156) */}
      {SHOW_VIEW_TOGGLE && <ViewToggleRow onClick={onOpenViewSheet} />}

      {/* iter155 — Ride/Discover mode toggle */}
      <ModeToggleRow onClose={onClose} />

      <div className="my-1 mx-4 h-px bg-gold-soft" />

      {/* Get Listed (gold, always visible) */}
      <button
        type="button"
        role="menuitem"
        data-testid="profile-menu-get-listed"
        onClick={onOpenGetListedSheet}
        className="w-full flex items-center gap-3 px-4 transition-colors hover:bg-[rgba(200,160,99,0.08)]"
        style={{ minHeight: 48 }}
      >
        <Rocket className="w-[18px] h-[18px] shrink-0" strokeWidth={1.5} style={{ color: "#E8B84B" }} />
        <span
          className="flex-1 text-left"
          style={{ color: "#E8B84B", fontFamily: "'DM Sans', sans-serif", fontSize: 15, fontWeight: 600 }}
        >
          {t("menu_get_listed")}
        </span>
      </button>

      {/* Save for Offline */}
      <SaveForOfflineRow t={t} onClose={onClose} />

      {/* Settings */}
      <SimpleMenuItem to="/settings" icon={SettingsIcon} onClose={onClose} testid="profile-menu-settings">
        {t("menu_settings")}
      </SimpleMenuItem>

      {/* Language row — full wording, tap to switch */}
      <LanguageRow />

      {/* iter141 — Get eSIM / SIM (opens existing /connected page) */}
      <EsimRow onClose={onClose} />

      <div className="my-1 mx-4 h-px bg-gold-soft" />

      {/* Log In */}
      <button
        type="button"
        data-testid="profile-menu-login"
        onClick={onSignIn}
        className="w-full flex items-center gap-3 px-4 text-[15px] text-foreground/85 hover:bg-[rgba(200,160,99,0.05)] hover:text-foreground transition-colors"
        style={{ minHeight: 48 }}
      >
        <LogIn className="w-[18px] h-[18px] text-gold shrink-0" strokeWidth={1.5} />
        <span className="flex-1">{t("auth_log_in")}</span>
      </button>

      {/* Privacy / Terms footer (10px allowed per design rules) */}
      <div className="px-4 py-2 flex items-center justify-center gap-4 border-t border-gold-soft"
        style={{ fontSize: 10, color: "rgba(245,241,232,0.45)" }}>
        <Link to="/privacy" data-testid="profile-menu-privacy" className="hover:text-foreground transition-colors">{t("privacy")}</Link>
        <span>·</span>
        <Link to="/terms" data-testid="profile-menu-terms" className="hover:text-foreground transition-colors">{t("terms")}</Link>
      </div>
    </>
  );
}

// ============================================================================
// AuthedPanel — same shape as GuestPanel but with identity + role dashboards
// + Sign Out instead of Log In. ⚠️ MUST keep every section.
// ============================================================================
function AuthedPanel({ t, user, name, avatar, phone, pendingBadge, myBiz, onClose, onLogOut, onOpenViewSheet, onOpenBizSheet, onOpenGetListedSheet }) {
  const role = user?.role;
  const nav = useNavigate();

  const onBusinessClick = () => {
    const list = Array.isArray(myBiz) ? myBiz : [];
    if (list.length === 1) { nav(`/business/${list[0].id}`); onClose?.(); }
    else if (list.length > 1) { onOpenBizSheet?.(); }
    else { nav("/business"); onClose?.(); }
  };

  return (
    <>
      {/* Identity header */}
      <div className="flex items-center gap-3 px-4 py-4 border-b border-gold-soft" data-testid="profile-menu-identity">
        {avatar ? (
          <img src={avatar} alt="" className="w-10 h-10 rounded-full object-cover ring-1 ring-[rgba(200,160,99,0.4)]" />
        ) : (
          <span className="w-10 h-10 grid place-items-center rounded-full border border-gold-soft text-gold"
            style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 14, fontWeight: 600 }}>
            {(name || "?")[0].toUpperCase()}
          </span>
        )}
        <div className="min-w-0 flex-1">
          <div className="font-display text-[15px] leading-tight text-foreground truncate"
            style={{ fontFamily: "'Playfair Display', serif" }} data-testid="profile-menu-name">
            {name}
          </div>
          <div className="text-[14px] text-foreground/55 mt-0.5 truncate"
            style={{ fontFamily: "'DM Sans', sans-serif" }} data-testid="profile-menu-phone">
            {phone || (user?.email ? user.email : t("user_role_traveler"))}
          </div>
        </div>
      </div>

      {/* Local / Tourist toggle — hidden via SHOW_VIEW_TOGGLE flag (iter156) */}
      {SHOW_VIEW_TOGGLE && <ViewToggleRow onClick={onOpenViewSheet} />}

      {/* iter155 — Ride/Discover mode toggle */}
      <ModeToggleRow onClose={onClose} />

      <div className="my-1 mx-4 h-px bg-gold-soft" />

      {/* Role dashboards */}
      {role === "vendor" && (
        <SimpleMenuItem to="/vendor" icon={Store} onClose={onClose} testid="profile-menu-vendor-dashboard">
          <span className="flex-1 flex items-center gap-2">
            {t("menu_vendor_dashboard")}
            <Badge tone="active" label={t("badge_active")} testid="profile-menu-vendor-badge" />
          </span>
        </SimpleMenuItem>
      )}
      {role === "business" && (
        <ActionMenuItem onClick={onBusinessClick} icon={Building2} testid="profile-menu-business-dashboard">
          <span className="flex-1 flex items-center gap-2">
            {t("menu_business_dashboard")}
            <Badge tone="active" label={t("badge_active")} testid="profile-menu-business-badge" />
          </span>
        </ActionMenuItem>
      )}
      {role === "admin" && (
        <SimpleMenuItem to="/admin" icon={Shield} onClose={onClose} testid="profile-menu-admin-dashboard-link">
          {t("role_admin") || "Admin"}
        </SimpleMenuItem>
      )}

      {pendingBadge?.status === "pending" && (
        <button
          type="button"
          onClick={() => { toast(t("app_pending_review"), { duration: 5000 }); }}
          data-testid="profile-menu-application-pending"
          className="w-full flex items-center gap-3 px-4 text-[15px] text-foreground/85 hover:bg-[rgba(200,160,99,0.05)] transition-colors"
          style={{ minHeight: 48 }}
        >
          <Clock className="w-[18px] h-[18px] text-gold shrink-0" strokeWidth={1.5} />
          <span className="flex-1 text-left flex items-center gap-2">
            {t("menu_application_pending")}
            <Badge tone="pending" label={t("badge_pending")} testid="profile-menu-app-pending-badge" />
          </span>
        </button>
      )}

      {(role === "vendor" || role === "business" || role === "admin" || pendingBadge?.status === "pending") && (
        <div className="my-1 mx-4 h-px bg-gold-soft" />
      )}

      {/* Get Listed */}
      <button
        type="button"
        role="menuitem"
        data-testid="profile-menu-get-listed"
        onClick={onOpenGetListedSheet}
        className="w-full flex items-center gap-3 px-4 transition-colors hover:bg-[rgba(200,160,99,0.08)]"
        style={{ minHeight: 48 }}
      >
        <Rocket className="w-[18px] h-[18px] shrink-0" strokeWidth={1.5} style={{ color: "#E8B84B" }} />
        <span
          className="flex-1 text-left"
          style={{ color: "#E8B84B", fontFamily: "'DM Sans', sans-serif", fontSize: 15, fontWeight: 600 }}
        >
          {t("menu_get_listed")}
        </span>
      </button>

      {/* Save for Offline */}
      <SaveForOfflineRow t={t} onClose={onClose} />

      {/* Settings */}
      <SimpleMenuItem to="/settings" icon={SettingsIcon} onClose={onClose} testid="profile-menu-settings">
        {t("menu_settings")}
      </SimpleMenuItem>

      {/* Language row */}
      <LanguageRow />

      {/* iter141 — Get eSIM / SIM (opens existing /connected page) */}
      <EsimRow onClose={onClose} />

      <div className="my-1 mx-4 h-px bg-gold-soft" />

      {/* Sign Out */}
      <button
        type="button"
        data-testid="profile-menu-logout"
        onClick={onLogOut}
        className="w-full flex items-center gap-3 px-4 text-[15px] text-foreground/85 hover:bg-[rgba(200,160,99,0.05)] hover:text-foreground transition-colors"
        style={{ minHeight: 48 }}
      >
        <LogOut className="w-[18px] h-[18px] shrink-0" strokeWidth={1.5} style={{ color: "#E66B6B" }} />
        <span className="flex-1" style={{ color: "#E66B6B" }}>{t("auth_log_out")}</span>
      </button>

      <div className="px-4 py-2 flex items-center justify-center gap-4 border-t border-gold-soft"
        style={{ fontSize: 10, color: "rgba(245,241,232,0.45)" }}>
        <Link to="/privacy" data-testid="profile-menu-privacy" className="hover:text-foreground transition-colors" onClick={onClose}>{t("privacy")}</Link>
        <span>·</span>
        <Link to="/terms" data-testid="profile-menu-terms" className="hover:text-foreground transition-colors" onClick={onClose}>{t("terms")}</Link>
      </div>
    </>
  );
}

/* ────────────────────────────────────────────────────────────────────────── */
/* Local/Tourist toggle row — tapping opens the ViewSelectorSheet.            */
/* ────────────────────────────────────────────────────────────────────────── */
function ViewToggleRow({ onClick }) {
  const { cohort } = useAudienceSettings();
  const { t } = useI18n();
  const isTourist = cohort !== "local";

  return (
    <button
      type="button"
      onClick={onClick}
      data-testid="profile-menu-view-toggle"
      aria-pressed={isTourist}
      aria-haspopup="dialog"
      className="w-full flex items-center justify-between gap-3 px-4 transition-colors hover:bg-white/[0.03]"
      style={{ minHeight: 48 }}
    >
      <span
        data-testid="profile-menu-view-toggle-local"
        className="inline-flex items-center gap-1.5"
        style={{ color: isTourist ? "#B0AAA3" : "#E8B84B", fontFamily: "'DM Sans', sans-serif", fontSize: 14, fontWeight: 500 }}
      >
        <Home style={{ width: 14, height: 14 }} strokeWidth={1.75} />
        {t("view_local_title")}
      </span>
      <Switch on={isTourist} />
      <span
        data-testid="profile-menu-view-toggle-tourist"
        className="inline-flex items-center gap-1.5"
        style={{ color: isTourist ? "#E8B84B" : "#B0AAA3", fontFamily: "'DM Sans', sans-serif", fontSize: 14, fontWeight: 500 }}
      >
        <Luggage style={{ width: 14, height: 14 }} strokeWidth={1.75} />
        {t("view_tourist_title")}
      </span>
    </button>
  );
}

function Switch({ on }) {
  return (
    <span
      aria-hidden="true"
      data-testid="profile-menu-view-toggle-switch"
      data-state={on ? "on" : "off"}
      style={{
        position: "relative", display: "inline-block",
        width: 40, height: 22, borderRadius: 11,
        background: on ? "rgba(232,184,75,0.25)" : "#2a2a2a",
        border: on ? "0.5px solid rgba(232,184,75,0.5)" : "0.5px solid #333",
        transition: "background-color 0.2s ease, border-color 0.2s ease",
        flex: "0 0 40px",
      }}
    >
      <span style={{
        position: "absolute", top: 2, left: on ? 22 : 2,
        width: 16, height: 16, borderRadius: 9999,
        background: on ? "#E8B84B" : "#555",
        transition: "left 0.2s ease, background-color 0.2s ease",
      }} />
    </span>
  );
}

/* iter155 — Discover ↔ Ride mode toggle row.
 * iter169 — Exported so it can be reused on My QR (and any other
 * screen) with the exact same styling/behavior. Consumers that don't
 * need the dropdown-close side-effect can omit `onClose`.
 * Tapping anywhere on the row flips the app mode; the row is fully
 * visible in both modes (so the user can always get back).
 */
export function ModeToggleRow({ onClose }) {
  const [mode, , toggle] = useMode();
  const { lang } = useI18n();
  // iter170 — Hide the row entirely when the global Discover switch is
  // OFF (and the current user isn't admin). This cascades to every
  // consumer including MyQr's inline mode toggle.
  const discoverAvailable = useDiscoverAvailable();
  if (!discoverAvailable) return null;
  const isRide = mode === "ride";
  const handle = () => {
    toggle();
    onClose?.();
  };
  return (
    <button
      type="button"
      onClick={handle}
      data-testid="profile-menu-mode-toggle"
      aria-pressed={isRide}
      className="w-full flex items-center justify-between gap-3 px-4 transition-colors hover:bg-white/[0.03]"
      style={{ minHeight: 48 }}
    >
      <span
        data-testid="profile-menu-mode-toggle-discover"
        className="inline-flex items-center gap-1.5"
        style={{ color: isRide ? "#B0AAA3" : "#E8B84B", fontFamily: "'DM Sans', sans-serif", fontSize: 14, fontWeight: 500 }}
      >
        <Compass style={{ width: 14, height: 14 }} strokeWidth={1.75} />
        {lang === "es" ? "Descubrir" : "Discover"}
      </span>
      <Switch on={isRide} />
      <span
        data-testid="profile-menu-mode-toggle-ride"
        className="inline-flex items-center gap-1.5"
        style={{ color: isRide ? "#E8B84B" : "#B0AAA3", fontFamily: "'DM Sans', sans-serif", fontSize: 14, fontWeight: 500 }}
      >
        <Car style={{ width: 14, height: 14 }} strokeWidth={1.75} />
        {lang === "es" ? "Viaje" : "Ride"}
      </span>
    </button>
  );
}

function SaveForOfflineRow({ t, onClose }) {
  const nav = useNavigate();
  const [saved, setSaved] = useState(false);
  useEffect(() => {
    try {
      const v = localStorage.getItem("habano.offline.savedAt");
      setSaved(!!v);
    } catch {/* ignore */}
  }, []);
  const go = () => { nav("/offline"); onClose?.(); };
  return (
    <button
      type="button"
      data-testid="profile-menu-save-offline"
      onClick={go}
      className="w-full flex items-center gap-3 px-4 text-[15px] text-foreground/85 hover:bg-[rgba(200,160,99,0.05)] transition-colors"
      style={{ minHeight: 48 }}
    >
      {saved ? (
        <CheckCircle2 className="w-[18px] h-[18px] shrink-0" strokeWidth={1.5} style={{ color: "#7BC97B" }} />
      ) : (
        <Download className="w-[18px] h-[18px] text-gold shrink-0" strokeWidth={1.5} />
      )}
      <span className="flex-1 text-left">
        {saved ? `✓ ${t("menu_save_for_offline_done")}` : t("menu_save_for_offline")}
      </span>
    </button>
  );
}

function Badge({ tone, label, testid }) {
  const colors = tone === "pending"
    ? { bg: "rgba(245,158,11,0.14)", border: "rgba(245,158,11,0.4)", color: "#F5A623" }
    : { bg: "rgba(123,201,123,0.14)", border: "rgba(123,201,123,0.4)", color: "#7BC97B" };
  return (
    <span
      data-testid={testid}
      style={{
        background: colors.bg,
        border: `1px solid ${colors.border}`,
        color: colors.color,
        fontFamily: "'DM Sans', sans-serif",
        fontSize: 10,
        fontWeight: 700,
        letterSpacing: "0.12em",
        padding: "2px 6px",
        borderRadius: 3,
      }}
    >
      {label}
    </span>
  );
}

function SimpleMenuItem({ to, icon: Icon, children, onClose, testid }) {
  return (
    <Link
      to={to}
      role="menuitem"
      data-testid={testid || `profile-menu-${(children || "").toString().toLowerCase().replace(/\s+/g, "-")}`}
      onClick={onClose}
      className="flex items-center gap-3 px-4 text-[15px] text-foreground/85 hover:bg-[rgba(200,160,99,0.05)] hover:text-foreground transition-colors"
      style={{ minHeight: 48 }}
    >
      <Icon className="w-[18px] h-[18px] text-gold shrink-0" strokeWidth={1.5} />
      <span className="flex-1">{children}</span>
    </Link>
  );
}

function ActionMenuItem({ onClick, icon: Icon, children, testid }) {
  return (
    <button
      type="button"
      role="menuitem"
      data-testid={testid}
      onClick={onClick}
      className="w-full flex items-center gap-3 px-4 text-[15px] text-foreground/85 hover:bg-[rgba(200,160,99,0.05)] hover:text-foreground transition-colors"
      style={{ minHeight: 48 }}
    >
      <Icon className="w-[18px] h-[18px] text-gold shrink-0" strokeWidth={1.5} />
      <span className="flex-1 text-left">{children}</span>
    </button>
  );
}

// ============================================================================
// EsimRow — opens the existing /connected eSIM / SIM page in the app.
// Added iter141 alongside Language; does NOT replace or modify any
// existing menu item, nor the /connected page itself. Reuses the exact
// styling of other menu rows so the dropdown stays visually consistent.
// ============================================================================
function EsimRow({ onClose }) {
  const { lang } = useI18n();
  return (
    <Link
      to="/connected"
      onClick={onClose}
      role="menuitem"
      data-testid="profile-menu-esim"
      className="w-full flex items-center gap-3 px-4 text-[15px] text-foreground/85 hover:bg-[rgba(200,160,99,0.05)] hover:text-foreground transition-colors"
      style={{ minHeight: 48 }}
    >
      <Smartphone className="w-[18px] h-[18px] text-gold shrink-0" strokeWidth={1.5} />
      <span className="flex-1">
        {lang === "es" ? "Obtener eSIM / SIM" : "Get eSIM / SIM"}
      </span>
    </Link>
  );
}
//   • lang === "en" → "English → Español"
//   • lang === "es" → "Español → English"
// Tapping anywhere on the row switches language instantly.
// ============================================================================
function LanguageRow() {
  const { lang, setLang } = useI18n();
  const target = lang === "es" ? "en" : "es";
  const currentLabel = lang === "es" ? "Español" : "English";
  const otherLabel = target === "en" ? "English" : "Español";
  const aria = target === "en" ? "Switch to English" : "Cambiar a Español";

  return (
    <button
      type="button"
      onClick={() => setLang(target)}
      aria-label={aria}
      data-testid="profile-menu-language-row"
      className="w-full flex items-center gap-3 px-4 transition-colors hover:bg-[rgba(200,160,99,0.05)]"
      style={{ minHeight: 48 }}
    >
      <Globe className="w-[18px] h-[18px] shrink-0" strokeWidth={1.5} style={{ color: "#E8B84B" }} />
      <span
        className="flex-1 text-left flex items-center gap-2"
        style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 15, color: "#F5F1E8" }}
      >
        <span data-testid="profile-menu-language-current">{currentLabel}</span>
        <ArrowRight className="w-3 h-3" strokeWidth={1.75} style={{ color: "#B0AAA3" }} />
        <span data-testid="profile-menu-language-other" style={{ color: "#E8B84B", fontWeight: 600 }}>
          {otherLabel}
        </span>
      </span>
    </button>
  );
}
